import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { logger } from './utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REC_LABELS = {
  strong_yes: { label: 'Strong Yes', color: 'bg-emerald-100 text-emerald-800' },
  yes: { label: 'Yes', color: 'bg-green-100 text-green-700' },
  hold: { label: 'Hold', color: 'bg-amber-100 text-amber-700' },
  no: { label: 'No', color: 'bg-rose-100 text-rose-700' },
  strong_no: { label: 'Strong No', color: 'bg-red-100 text-red-800' },
};

export function ScorecardPanel({ applicationId }) {
  const [cards, setCards] = useState([]);
  const [templates, setTemplates] = useState({});
  const [adding, setAdding] = useState(false);
  const [roleKey, setRoleKey] = useState('default');
  const [scores, setScores] = useState([]);
  const [recommendation, setRecommendation] = useState('yes');
  const [strengths, setStrengths] = useState('');
  const [concerns, setConcerns] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/scorecards/application/${applicationId}`),
        axios.get(`${API}/scorecards/competencies`),
      ]);
      setCards(Array.isArray(a.data) ? a.data : []);
      setTemplates(b.data || {});
    } catch (err) { logger.error('load scorecards', err); }
  }, [applicationId]);

  useEffect(() => { load(); }, [load]);

  const initScores = (key) => {
    const list = templates[key] || templates.default || [];
    setScores(list.map((name) => ({ name, rating: 3, notes: '' })));
  };

  const startAdd = () => {
    setAdding(true);
    initScores(roleKey);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/scorecards`, {
        application_id: applicationId,
        competencies: scores,
        recommendation,
        strengths,
        concerns,
      });
      setAdding(false);
      setStrengths(''); setConcerns(''); setRecommendation('yes');
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save scorecard');
    } finally { setBusy(false); }
  };

  return (
    <div className="p-5 border-t" data-testid="scorecard-panel">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-sm">Scorecards ({cards.length})</h3>
        {!adding && (
          <button data-testid="add-scorecard-btn" onClick={startAdd} className="text-xs px-2 py-1 bg-indigo-600 text-white rounded">
            + Scorecard
          </button>
        )}
      </div>

      {adding && (
        <form onSubmit={submit} className="space-y-3 mb-4 p-3 bg-gray-50 rounded text-sm">
          <div>
            <label className="block text-xs font-medium mb-1">Competency template</label>
            <select
              data-testid="scorecard-role-select"
              value={roleKey}
              onChange={(e) => { setRoleKey(e.target.value); initScores(e.target.value); }}
              className="w-full px-2 py-1 border rounded text-sm"
            >
              {Object.keys(templates).map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </div>
          {scores.map((s, idx) => (
            <div key={s.name} className="bg-white p-2 rounded border">
              <div className="flex justify-between items-center mb-1">
                <span className="font-medium text-xs">{s.name}</span>
                <span className="text-xs text-gray-500" data-testid={`score-value-${idx}`}>{s.rating}/5</span>
              </div>
              <input
                type="range" min="1" max="5" value={s.rating}
                data-testid={`score-slider-${idx}`}
                onChange={(e) => {
                  const next = [...scores]; next[idx] = { ...next[idx], rating: parseInt(e.target.value, 10) }; setScores(next);
                }}
                className="w-full"
              />
              <input
                placeholder="Notes (optional)"
                value={s.notes || ''}
                onChange={(e) => { const next = [...scores]; next[idx] = { ...next[idx], notes: e.target.value }; setScores(next); }}
                className="w-full mt-1 px-2 py-1 border rounded text-xs"
              />
            </div>
          ))}
          <div>
            <label className="block text-xs font-medium mb-1">Recommendation</label>
            <select
              data-testid="scorecard-recommendation"
              value={recommendation}
              onChange={(e) => setRecommendation(e.target.value)}
              className="w-full px-2 py-1 border rounded text-sm"
            >
              {Object.entries(REC_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <textarea placeholder="Key strengths" value={strengths} onChange={(e) => setStrengths(e.target.value)} className="w-full px-2 py-1 border rounded text-xs" rows="2" />
          <textarea placeholder="Concerns" value={concerns} onChange={(e) => setConcerns(e.target.value)} className="w-full px-2 py-1 border rounded text-xs" rows="2" />
          <div className="flex gap-2">
            <button type="submit" disabled={busy} data-testid="save-scorecard-btn" className="px-3 py-1 bg-indigo-600 text-white rounded text-xs disabled:opacity-50">
              {busy ? 'Saving…' : 'Save scorecard'}
            </button>
            <button type="button" onClick={() => setAdding(false)} className="px-3 py-1 text-gray-600 rounded text-xs">Cancel</button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {cards.map((c) => {
          const rec = REC_LABELS[c.recommendation] || { label: c.recommendation, color: 'bg-gray-100' };
          return (
            <div key={c.id} className="p-3 bg-white border rounded" data-testid={`scorecard-${c.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs text-gray-500">{c.interviewer_name || c.interviewer_email}</p>
                  <p className="text-sm font-semibold">⭐ {c.average_rating}/5</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${rec.color}`}>{rec.label}</span>
              </div>
              {c.strengths && <p className="text-xs mt-2"><strong>Strengths:</strong> {c.strengths}</p>}
              {c.concerns && <p className="text-xs mt-1 text-rose-700"><strong>Concerns:</strong> {c.concerns}</p>}
            </div>
          );
        })}
        {cards.length === 0 && !adding && <p className="text-xs text-gray-500">No scorecards yet.</p>}
      </div>
    </div>
  );
}


export function OfferPanel({ applicationId }) {
  const [offers, setOffers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    salary_amount: 75000, salary_currency: 'GBP', bonus_pct: 10,
    start_date: '', expires_in_days: 7, location: '', notes_to_candidate: '',
  });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/offers/application/${applicationId}`);
      setOffers(data || []);
    } catch (err) { logger.error('load offers', err); }
  }, [applicationId]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/offers`, { ...form, application_id: applicationId, salary_amount: Number(form.salary_amount), bonus_pct: form.bonus_pct ? Number(form.bonus_pct) : null });
      setCreating(false);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create offer');
    } finally { setBusy(false); }
  };

  const sendOffer = async (id) => {
    if (!window.confirm('Send this offer to the candidate?')) return;
    await axios.post(`${API}/offers/${id}/send`);
    load();
  };

  const revokeOffer = async (id) => {
    if (!window.confirm('Revoke this offer? The candidate will no longer be able to accept it.')) return;
    await axios.post(`${API}/offers/${id}/revoke`);
    load();
  };

  return (
    <div className="p-5 border-t" data-testid="offer-panel">
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-semibold text-sm">Offers ({offers.length})</h3>
        {!creating && (
          <button data-testid="add-offer-btn" onClick={() => setCreating(true)} className="text-xs px-2 py-1 bg-emerald-600 text-white rounded">
            + Offer
          </button>
        )}
      </div>

      {creating && (
        <form onSubmit={create} className="space-y-2 mb-4 p-3 bg-gray-50 rounded text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium">Salary</label>
              <input required type="number" min="1" value={form.salary_amount} onChange={(e) => setForm({ ...form, salary_amount: e.target.value })} data-testid="offer-salary" className="w-full px-2 py-1 border rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium">Currency</label>
              <select value={form.salary_currency} onChange={(e) => setForm({ ...form, salary_currency: e.target.value })} className="w-full px-2 py-1 border rounded text-sm">
                <option>GBP</option><option>USD</option><option>EUR</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium">Bonus %</label>
              <input type="number" min="0" max="200" value={form.bonus_pct} onChange={(e) => setForm({ ...form, bonus_pct: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium">Expires (days)</label>
              <input type="number" min="1" max="60" value={form.expires_in_days} onChange={(e) => setForm({ ...form, expires_in_days: parseInt(e.target.value, 10) })} className="w-full px-2 py-1 border rounded text-sm" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium">Start date</label>
            <input required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="offer-start-date" className="w-full px-2 py-1 border rounded text-sm" />
          </div>
          <input placeholder="Location (optional)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full px-2 py-1 border rounded text-sm" />
          <textarea placeholder="Notes to candidate" rows="2" value={form.notes_to_candidate} onChange={(e) => setForm({ ...form, notes_to_candidate: e.target.value })} className="w-full px-2 py-1 border rounded text-xs" />
          <div className="flex gap-2">
            <button type="submit" disabled={busy} data-testid="save-offer-btn" className="px-3 py-1 bg-emerald-600 text-white rounded text-xs disabled:opacity-50">
              {busy ? 'Creating…' : 'Save as draft'}
            </button>
            <button type="button" onClick={() => setCreating(false)} className="px-3 py-1 text-gray-600 rounded text-xs">Cancel</button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {offers.map((o) => {
          const statusColor = {
            draft: 'bg-gray-200 text-gray-700',
            sent: 'bg-blue-100 text-blue-700',
            accepted: 'bg-emerald-100 text-emerald-800',
            declined: 'bg-rose-100 text-rose-700',
            revoked: 'bg-zinc-200 text-zinc-700',
            expired: 'bg-amber-100 text-amber-700',
          }[o.status] || 'bg-gray-100';
          return (
            <div key={o.id} className="p-3 bg-white border rounded text-sm" data-testid={`offer-${o.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold">{o.salary_currency} {Number(o.salary_amount).toLocaleString()}{o.bonus_pct ? ` + ${o.bonus_pct}% bonus` : ''}</p>
                  <p className="text-xs text-gray-500">Start {o.start_date} · Expires {o.expires_at?.slice(0, 10)}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${statusColor}`} data-testid={`offer-status-${o.id}`}>{o.status}</span>
              </div>
              {o.status === 'draft' && (
                <button data-testid={`send-offer-${o.id}`} onClick={() => sendOffer(o.id)} className="mt-2 text-xs px-2 py-1 bg-emerald-600 text-white rounded">Send to candidate</button>
              )}
              {o.status === 'sent' && (
                <button onClick={() => revokeOffer(o.id)} className="mt-2 text-xs px-2 py-1 bg-rose-600 text-white rounded">Revoke</button>
              )}
            </div>
          );
        })}
        {offers.length === 0 && !creating && <p className="text-xs text-gray-500">No offers yet.</p>}
      </div>
    </div>
  );
}
